import i18n from '../i18n';

// 默认坐标（东京站丸之内南口）
export const DEFAULT_COORDINATE = {
  latitude: 35.6806794,
  longitude: 139.7653005
};

let loadingPromise: Promise<void> | null = null;

interface PlaceWithDistance extends mapkit.Place {
  id?: string;
  distance: number;
}

export function loadMapkit(token: string, language = 'zh-Hans') {
  if (loadingPromise !== null) {
    return loadingPromise;
  }

  loadingPromise = new Promise(resolve => {
    const script = document.createElement('script');
    script.addEventListener(
      'load',
      () => {
        mapkit.init({
          authorizationCallback: done => done(token),
          language
        });

        resolve();
      },
      { once: true }
    );
    script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
  });
  return loadingPromise;
}

export function getJapanesePlace(id: string): Promise<string> {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const lookup = new mapkit.PlaceLookup({
      language: 'ja-JP'
    });
    lookup.getPlace(id, (error: Error, place: mapkit.Place) => {
      if (error) {
        reject(error);
      } else {
        resolve(place?.name || '');
      }
    });
  });
}

// 计算两个坐标点之间的距离
export function calculateDistance(
  coord1: { latitude: number; longitude: number },
  coord2: { latitude: number; longitude: number }
): number {
  const R = 6371; // 地球半径（千米）
  const lat1 = (coord1.latitude * Math.PI) / 180;
  const lat2 = (coord2.latitude * Math.PI) / 180;
  const deltaLat = ((coord2.latitude - coord1.latitude) * Math.PI) / 180;
  const deltaLon = ((coord2.longitude - coord1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// 将距离转换为公里
export function convertDistanceToKilometers(
  distance: number | undefined
): string {
  if (distance === undefined) {
    return '';
  }
  if (distance < 1) {
    return `${(distance * 1000).toFixed(0)}m`;
  }
  return `${distance.toFixed(1)}km`;
}

export function convertDistanceToKilometersZh(distance: number | undefined): {
  distance: string;
  unit: string;
} {
  if (distance === undefined) {
    return {
      distance: '',
      unit: ''
    };
  }
  if (distance < 1) {
    return {
      distance: `${(distance * 1000).toFixed(0)}`,
      unit: i18n.t('views.orderDetail.meter')
    };
  }
  return {
    distance: `${distance.toFixed(1)}`,
    unit: i18n.t('views.orderDetail.kilometers')
  };
}

// 根据与中心点的距离对地点进行排序
export function sortPlacesByDistance(
  places: mapkit.Place[],
  validIds: string[],
  centerCoordinate: mapkit.Coordinate
): mapkit.Place[] {
  return places
    .filter(place => validIds.includes((place as PlaceWithDistance).id || ''))
    .map(place => {
      // 计算距离的绝对值，可用于对比测试
      const distance = Math.abs(
        calculateDistance(
          {
            latitude: place.coordinate.latitude,
            longitude: place.coordinate.longitude
          },
          {
            latitude: centerCoordinate.latitude,
            longitude: centerCoordinate.longitude
          }
        )
      );
      return Object.assign(place, { distance });
    })
    .sort(
      (pre, next) =>
        (pre as PlaceWithDistance).distance -
        (next as PlaceWithDistance).distance
    );
}

// 反向地理编码
export function reverseGeocode(
  coordinate: mapkit.Coordinate,
  options: {
    language?: string;
  } = {}
): Promise<{
  name: string;
  nameJa: string;
  address: string;
  mapId?: string;
  latitude: number;
  longitude: number;
  administrativeArea: string;
  locality: string;
  canUse: boolean;
}> {
  const { language = 'ja' } = options;
  const geocoder = new mapkit.Geocoder({ language });

  return new Promise((resolve, reject) => {
    geocoder.reverseLookup(coordinate, async (error, data) => {
      if (error) {
        console.error('reverseLookup error:', error);
        reject(error);
        return;
      }

      if (data?.results?.length > 0) {
        const result = data.results[0];
        const resultId = (result as { id?: string })?.id;

        const formattedAddress = result?.formattedAddress?.split(',') ?? [];

        const name =
          formattedAddress.length > 3
            ? formattedAddress[0] || ''
            : formattedAddress.slice(-2).join(', ');
        resolve({
          name: name || '',
          nameJa: name || '',
          address: result.formattedAddress || '',
          mapId: resultId,
          latitude: result.coordinate.latitude,
          longitude: result.coordinate.longitude,
          administrativeArea: result.administrativeArea || '',
          locality: result.locality || '',
          canUse: false
        });
      } else {
        resolve({
          name: '',
          nameJa: '',
          address: '',
          mapId: undefined,
          latitude: coordinate.latitude,
          longitude: coordinate.longitude,
          administrativeArea: '',
          locality: '',
          canUse: false
        });
      }
    });
  });
}

// 获取地图中心点坐标
export function getMapCenterCoordinate(mapInstance: {
  map?: mapkit.Map | null;
}): mapkit.Coordinate | null {
  const mapElement = mapInstance?.map?.element;
  if (mapElement) {
    // 获取地图容器的位置和大小
    const rect = mapElement.getBoundingClientRect();
    // 计算地图容器的中心点
    const centerPoint = new DOMPoint(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2
    );
    return (
      mapInstance?.map?.convertPointOnPageToCoordinate(centerPoint) || null
    );
  }
  return null;
}
